// ============================================================
// 📁 src/i18n/index.ts — چندزبانه‌ی کل سیستم (دری/فارسی، عربی، پښتو، کوردیی ناوەندی)
// کلید ترجمه = همان متن فارسی. زبان با «بارگذاری دوباره‌ی صفحه» عوض می‌شود، پس t() هر جایی (حتی بالای فایل) درست کار می‌کند.
// ============================================================
export type Lang = "fa" | "ar" | "ps" | "ckb";
export const LANGS: { code: Lang; name: string; sub: string }[] = [
  { code: "fa", name: "دری / فارسی", sub: "Dari / Persian" },
  { code: "ps", name: "پښتو", sub: "Pashto" },
  { code: "ar", name: "العربية", sub: "Arabic" },
  { code: "ckb", name: "کوردیی ناوەندی", sub: "Central Kurdish — Sorani" },
];
export const LANG_KEY = "app_lang";
declare const process: { env: { REACT_APP_API_URL?: string } };
const BASE = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) || "/api";

let cur: Lang = "fa";
let D: Record<string, string> = {};
let PAT: { re: RegExp; order: number[]; val: string; lit: string }[] = [];
const MEMO = new Map<string, string>();
const MISSING = new Set<string>();

export const getLang = (): Lang => cur;
export const isLang = (x: any): x is Lang => x === "fa" || x === "ar" || x === "ps" || x === "ckb";
const LOADING_KEY = "در حال بارگذاری...";
const BRAND_LOADING = "سیستم مدیریتی و حسابداری کارا";
const ARABIC_SCRIPT = /[؀-ۿ]/;

/** ترجمه‌ی متن ثابت. آرگومان‌ها جای {0} و {1} … می‌نشینند. */
export function t(s: string, ...a: Array<string | number | null | undefined>): string {
  let r = s;
  if (cur !== "fa") {
    const v = D[s];
    if (v !== undefined) r = v; else if (s.length > 8 && ARABIC_SCRIPT.test(s) && /<[A-Za-z\/]/.test(s)) r = trHtmlM(s); else if (ARABIC_SCRIPT.test(s) && !MISSING.has(s)) { MISSING.add(s); try { (window as any).__i18nMissing = Array.from(MISSING); } catch { /* ignore */ } }
  }
  if (s === LOADING_KEY) r = r + "  ·  " + (cur !== "fa" && D[BRAND_LOADING] !== undefined ? D[BRAND_LOADING] : BRAND_LOADING);
  return a.length ? r.replace(/\{(\d+)\}/g, (_m, i) => { const x = a[Number(i)]; return x === null || x === undefined ? "" : String(x); }) : r;
}

/** نام جایگزین t (کدمورد خودکار از این نام استفاده می‌کند تا با متغیرهای محلی «t» تداخل نکند) */
export const _t = t;

/** ترجمه‌ی متن پویا (پیام سرور، توضیح ذخیره‌شده…): اول عین متن، بعد الگوهای دارای {0}. اگر پیدا نشد همان متن. */
export function tr(s: any): any {
  if (typeof s !== "string" || cur === "fa" || s === "" || !ARABIC_SCRIPT.test(s)) return s;
  const e = D[s]; if (e !== undefined) return e;
  const mm = MEMO.get(s); if (mm !== undefined) return mm;
  let out = s;
  const tt = s.trim();
  if (tt !== s && D[tt] !== undefined) out = s.replace(tt, D[tt]);
  else for (const p of PAT) {
    if (p.lit && s.indexOf(p.lit) < 0) continue;
    const m = p.re.exec(s);
    if (m) { out = p.val.replace(/\{(\d+)\}/g, (_x, i) => { const g = p.order.indexOf(Number(i)); return g >= 0 && m[g + 1] !== undefined ? tr(m[g + 1]) : ""; }); break; }
  }
  if (out === s && s.length > 8 && /<[A-Za-z\/]/.test(s)) out = trHtml(s);
  if (MEMO.size > 20000) MEMO.clear();
  MEMO.set(s, out); return out;
}
const HMEMO = new Map<string, string>();
function trHtmlM(s: string): string { let r = HMEMO.get(s); if (r === undefined) { r = trHtml(s); if (HMEMO.size > 3000) HMEMO.clear(); HMEMO.set(s, r); } return r; }
/** قالب HTML: فقط متن بین تگ‌ها و title/placeholder/alt/label ترجمه می‌شود؛ style و script دست نمی‌خورد */
function trHtml(s: string): string {
  const seg = (g: string) => {
    if (!ARABIC_SCRIPT.test(g)) return g;
    const lead = g.match(/^\s*/)![0], trail = g.match(/\s*$/)![0], core = g.trim();
    if (!core) return g;
    const hit = D[core] !== undefined ? D[core] : D[core.replace(/\s+/g, " ")];
    return hit !== undefined ? lead + hit + trail : g;
  };
  const parts = s.split(/(<(?:style|script)\b[\s\S]*?<\/(?:style|script)>|<[^>]*>)/i);
  return parts.map((p, i) => {
    if (i % 2 === 0) return seg(p);
    if (/^<(style|script)\b/i.test(p)) return p;
    return p.replace(/\b(title|placeholder|alt|label)="([^"]*)"/g, (_m, a, v) => a + '="' + seg(v) + '"');
  }).join("");
}
/** ترجمه‌ی همه‌ی رشته‌های پاسخ سرور (به‌جز فیلدهای فنی/شخصی). فقط وقتی عین متن یا الگو در دیکشنری باشد عوض می‌شود. */
const NO_TR = new Set(["serial", "path", "url", "attachment", "username", "email", "phone", "password", "token", "plate", "file", "filename", "file_name", "sha256", "ip", "link", "avatar", "logo"]);
export function trAll<T>(v: T): T {
  if (cur === "fa") return v;
  const walk = (x: any, k?: string): any => {
    if (typeof x === "string") return k && NO_TR.has(k) ? x : (k && FRAG_KEYS.has(k) ? trFrag(x) : tr(x));
    if (Array.isArray(x)) return x.map(i => walk(i, k));
    if (x && typeof x === "object") { const o: any = {}; for (const kk of Object.keys(x)) o[kk] = walk(x[kk], kk); return o; }
    return x;
  };
  return walk(v);
}

/* ---------- ترجمه‌ی تکه‌تکه (برای متن‌های ساخته‌شده با چسباندن چند تکه، مثل توضیح‌های خودکار سرور) ---------- */
let IDX: Map<string, string> | null = null;
function buildIdx() {
  IDX = new Map();
  for (const k of Object.keys(D)) { if (/\{\d+\}/.test(k)) continue; const n = k.trim().replace(/\s+/g, " "); if (n.length >= 2 && ARABIC_SCRIPT.test(n)) IDX.set(n, D[k].trim()); }
}
const FRAG_MEMO = new Map<string, string>();
export function trFrag(s: any): any {
  if (typeof s !== "string" || cur === "fa" || !ARABIC_SCRIPT.test(s)) return s;
  const whole = tr(s); if (whole !== s) return whole;
  const m = FRAG_MEMO.get(s); if (m !== undefined) return m;
  if (!IDX) buildIdx();
  const parts = s.split(/(\s+)/); // توکن، فاصله، توکن …
  const tok: string[] = []; const sp: string[] = [];
  parts.forEach((p, i) => { if (i % 2 === 0) tok.push(p); else sp.push(p); });
  const out: string[] = []; let i = 0;
  while (i < tok.length) {
    let hit = false;
    for (let n = Math.min(6, tok.length - i); n >= 1 && !hit; n--) {
      const seg = tok.slice(i, i + n).join(" ");
      const lead = (/^[(\[«"']+/.exec(seg) || [""])[0]; const trail = (/[،,؛;:.!؟?)\]»"'—-]+$/.exec(seg) || [""])[0];
      const core = seg.slice(lead.length, seg.length - trail.length);
      const v = core ? IDX!.get(core) : undefined;
      if (v !== undefined) { out.push(lead + v + trail); i += n; hit = true; if (i < tok.length) out.push(sp[i - 1] || " "); }
    }
    if (!hit) { out.push(tok[i]); i++; if (i < tok.length) out.push(sp[i - 1] || " "); }
  }
  const r = out.join("");
  if (FRAG_MEMO.size > 20000) FRAG_MEMO.clear();
  FRAG_MEMO.set(s, r); return r;
}
const FRAG_KEYS = new Set(["message", "notes", "note", "description", "desc", "title", "label", "hint", "text", "reason", "details", "detail", "subtitle", "status_label", "summary", "error", "msg", "name_ui"]);

let fetchPatched = false;
function installFetchTranslate() {
  if (fetchPatched || typeof window === "undefined" || !window.fetch) return;
  fetchPatched = true;
  const of = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const r = await of(...args);
    try {
      const ct = r.headers.get("content-type") || "";
      if (ct.indexOf("json") >= 0) { const oj = r.json.bind(r); (r as any).json = async () => trAll(await oj()); }
    } catch { /* ignore */ }
    return r;
  };
}
/** ترجمه‌ی همه‌ی رشته‌های یک ساختار فقط روی کلیدهای مشخص (برچسب‌ها، عنوان‌ها، پیام‌ها) */
export function trDeep<T>(v: T, keys: string[]): T {
  if (cur === "fa") return v;
  const walk = (x: any, k?: string): any => {
    if (typeof x === "string") return k && keys.includes(k) ? tr(x) : x;
    if (Array.isArray(x)) return x.map(i => walk(i, k));
    if (x && typeof x === "object") { const o: any = {}; for (const kk of Object.keys(x)) o[kk] = walk(x[kk], kk); return o; }
    return x;
  };
  return walk(v);
}
/** کلیدهایی از پاسخ سرور که متن نمایشی‌اند (داده‌ی کاربر نیستند) */
export const UI_KEYS = ["message", "title", "subtitle", "label", "sub", "desc", "hint", "notes", "note_ui", "status_label", "group_label", "text", "l", "placeholder", "confirm", "name_ui", "unit_label"];

export const monthNames = (): string[] => MONTHS[cur];
export const weekNames = (): string[] => WEEK[cur];
const MONTHS: Record<Lang, string[]> = {
  fa: ["حمل", "ثور", "جوزا", "سرطان", "اسد", "سنبله", "میزان", "عقرب", "قوس", "جدی", "دلو", "حوت"],
  ps: ["وری", "غویی", "غبرګولی", "چنګاښ", "زمری", "وږی", "تله", "لړم", "لیندۍ", "مرغومی", "سلواغه", "کب"],
  ar: ["الحمل", "الثور", "الجوزاء", "السرطان", "الأسد", "السنبلة", "الميزان", "العقرب", "القوس", "الجدي", "الدلو", "الحوت"],
  ckb: ["خاکەلێوە", "گوڵان", "جۆزەردان", "پووشپەڕ", "گەلاوێژ", "خەرمانان", "ڕەزبەر", "گەڵاڕێزان", "سەرماوەز", "بەفرانبار", "ڕێبەندان", "ڕەشەمە"],
};
const WEEK: Record<Lang, string[]> = { // یکشنبه … شنبه
  fa: ["یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنجشنبه", "جمعه", "شنبه"],
  ps: ["اتوار", "ګل", "نهه", "شورو", "زیارت", "جمعه", "خالي"],
  ar: ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"],
  ckb: ["یەکشەممە", "دووشەممە", "سێشەممە", "چوارشەممە", "پێنجشەممە", "هەینی", "شەممە"],
};

function compile(d: Record<string, string>) {
  PAT = [];
  for (const k of Object.keys(d)) {
    if (!/\{\d+\}/.test(k)) continue;
    const order: number[] = []; let src = "";
    k.split(/(\{\d+\})/).forEach(part => { const m = /^\{(\d+)\}$/.exec(part); if (m) { order.push(Number(m[1])); src += "(.+?)"; } else src += part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); });
    const lit = k.split(/\{\d+\}/).reduce((m, x) => (x.length > m.length ? x : m), "");
    try { PAT.push({ re: new RegExp("^" + src + "$"), order, val: d[k], lit }); } catch { /* ignore */ }
  }
  PAT.sort((a, b) => b.re.source.length - a.re.source.length);
}

const FONT_CSS = `
@font-face{font-family:'Vazirmatn';font-weight:400 500;font-style:normal;font-display:swap;src:url(/fonts/noto-sans-arabic-400.woff2) format('woff2');unicode-range:U+0679,U+067C,U+0688,U+0689,U+0693,U+0695,U+0696,U+069A,U+06A4,U+06AB,U+06B5,U+06BC,U+06C6,U+06CD,U+06CE,U+06D0,U+06D5,U+06CA,U+0626-0628}
@font-face{font-family:'Vazirmatn';font-weight:600 900;font-style:normal;font-display:swap;src:url(/fonts/noto-sans-arabic-700.woff2) format('woff2');unicode-range:U+0679,U+067C,U+0688,U+0689,U+0693,U+0695,U+0696,U+069A,U+06A4,U+06AB,U+06B5,U+06BC,U+06C6,U+06CD,U+06CE,U+06D0,U+06D5,U+06CA}
html[data-lang="ar"] body,html[data-lang="ps"] body,html[data-lang="ckb"] body{font-feature-settings:"liga" 1}
`;

export async function initI18n(): Promise<Lang> {
  let l: Lang = "fa"; let stored = "";
  try { stored = localStorage.getItem(LANG_KEY) || ""; } catch { /* ignore */ }
  if (isLang(stored)) l = stored;
  else {
    try {
      const ctl = new AbortController(); const to = setTimeout(() => ctl.abort(), 2500);
      const r = await fetch(`${BASE}/lang.php?action=get`, { signal: ctl.signal }); clearTimeout(to);
      const j = await r.json(); if (j && isLang(j.lang)) l = j.lang;
      try { localStorage.setItem(LANG_KEY, l); } catch { /* ignore */ }
    } catch { /* ignore */ }
  }
  cur = l;
  // هم‌خوانی با تنظیم شرکت (اگر در دستگاه دیگری عوض شده باشد) — بی‌صدا در پس‌زمینه
  if (isLang(stored)) fetch(`${BASE}/lang.php?action=get`).then(r => r.json()).then(j => { if (j && isLang(j.lang) && j.lang !== cur) { setLangLocal(j.lang); window.location.reload(); } }).catch(() => { /* ignore */ });
  if (l !== "fa") {
    for (let i = 0; i < 2; i++) {
      try { const r = await fetch(`/lang/${l}.json`, { cache: "no-cache" }); if (r.ok) { D = await r.json(); break; } } catch { /* retry */ }
    }
    compile(D);
    installFetchTranslate();
  }
  try {
    const de = document.documentElement; de.setAttribute("lang", l === "ckb" ? "ckb" : l); de.setAttribute("dir", "rtl"); de.setAttribute("data-lang", l);
    const st = document.createElement("style"); st.id = "i18n-fonts"; st.textContent = FONT_CSS; document.head.appendChild(st);
    document.title = t(document.title || "سیستم مدیریت");
  } catch { /* ignore */ }
  return l;
}

/** زبان شرکت را از سرور می‌خواند؛ اگر با زبان فعلی فرق داشت ذخیره می‌کند (و true برمی‌گرداند تا صفحه دوباره باز شود). */
export async function syncLangFromServer(token: string): Promise<boolean> {
  try {
    const r = await fetch(`${BASE}/lang.php`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ action: "get" }) });
    const j = await r.json();
    if (j && isLang(j.lang) && j.lang !== cur) { try { localStorage.setItem(LANG_KEY, j.lang); } catch { /* ignore */ } return true; }
  } catch { /* ignore */ }
  return false;
}
export function setLangLocal(l: Lang) { try { localStorage.setItem(LANG_KEY, l); } catch { /* ignore */ } }
